import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Package, Mail, Image, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Dashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    products: 0,
    contacts: 0,
    galleryItems: 0,
    features: 0,
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, contacts, gallery, features] = await Promise.all([
          axios.get(`${API_URL}/api/products`),
          axios.get(`${API_URL}/api/contact`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/gallery`),
          axios.get(`${API_URL}/api/features`),
        ]);

        setStats({
          products: products.data.length,
          contacts: contacts.data.length,
          galleryItems: gallery.data.items?.length || 0,
          features: features.data.features?.length || 0,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [API_URL, getAuthHeader]);

  const statCards = [
    {
      label: 'Products',
      value: stats.products,
      icon: Package,
      color: 'bg-blue-500',
      link: '/admin/products',
    },
    {
      label: 'Contact Submissions',
      value: stats.contacts,
      icon: Mail,
      color: 'bg-green-500',
      link: '/admin/contact',
    },
    {
      label: 'Gallery Items',
      value: stats.galleryItems,
      icon: Image,
      color: 'bg-purple-500',
      link: '/admin/gallery',
    },
    {
      label: 'Features',
      value: stats.features,
      icon: Award,
      color: 'bg-orange-500',
      link: '/admin/features',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Welcome Back!</h1>
        <p className="text-gray-400">
          Manage your Planeskeeper website content from here
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-gray-800 rounded-lg p-6 hover:bg-gray-750 transition-colors border border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-gray-400 text-sm font-medium">{stat.label}</h3>
              <p className="text-white text-3xl font-bold mt-2">{stat.value}</p>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-white text-xl font-bold mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/admin/products"
              className="block px-4 py-3 bg-gray-700 rounded-lg text-white hover:bg-gray-600 transition-colors"
            >
              Manage Products
            </Link>
            <Link
              to="/admin/hero"
              className="block px-4 py-3 bg-gray-700 rounded-lg text-white hover:bg-gray-600 transition-colors"
            >
              Edit Hero Section
            </Link>
            <Link
              to="/admin/gallery"
              className="block px-4 py-3 bg-gray-700 rounded-lg text-white hover:bg-gray-600 transition-colors"
            >
              Manage Gallery
            </Link>
            <Link
              to="/admin/settings"
              className="block px-4 py-3 bg-gray-700 rounded-lg text-white hover:bg-gray-600 transition-colors"
            >
              Site Settings
            </Link>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-white text-xl font-bold mb-4">Site Preview</h3>
          <p className="text-gray-400 mb-4">
            View your live website to see how your changes look
          </p>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-orange-600 rounded-lg text-white hover:bg-orange-700 transition-colors font-medium"
          >
            Open Website
          </a>
        </div>
      </div>
    </div>
  );
};
